import React, { useState } from 'react';
import useSWR from 'swr';
import api, { proxyImage, Coach } from '../services/api';
import ChatPanel from './ChatPanel';

interface CoachConv {
  conversation_id: number;
  created?: boolean;
}

/**
 * 电子教练页（v21）：左栏教练列表（SWR ['coaches']）+ 右栏单窗口对话。
 * 选中教练后取唯一会话 conversation_id（幂等端点），重置后换新 id。
 */
export default function CoachView() {
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);

  // 教练列表（缓存优先，后台刷新替换）
  const { data: coachesData } = useSWR<Coach[]>(
    ['coaches'],
    () => api.getCoaches().then(r => r.data.data || [])
  );
  const coaches = coachesData || [];

  // 选中教练的唯一会话（单对话窗口）
  const { data: convData, mutate: mutateConv } = useSWR<CoachConv | null>(
    selectedCoach ? ['coach-conversation', selectedCoach.id] : null,
    () => api.getCoachConversation(selectedCoach!.id).then(r => r.data)
  );

  const handleReset = async () => {
    if (!selectedCoach) return;
    if (!window.confirm(`确定重置与「${selectedCoach.name}」的对话吗？历史记录将被清空。`)) return;
    try {
      await api.resetCoachConversation(selectedCoach.id);
      // 重新拉取：reset 后 conversation_id 变化（旧会话已删除）
      mutateConv(undefined, { revalidate: true });
    } catch (e) {
      console.error('重置对话失败:', e);
      window.alert('重置失败，请稍后重试');
    }
  };

  return (
    <div className="kb-layout flex h-full min-h-0">
      {/* ── 左栏：教练列表 ── */}
      <aside className="upmaster-sidebar">
        <div className="upmaster-sidebar-header">
          <span className="upmaster-sidebar-title" style={{ fontWeight: 600 }}>🏋️ 我的教练</span>
        </div>
        <div className="upmaster-sidebar-list flex-1 overflow-y-auto">
          {coaches.length === 0 ? (
            <div className="upmaster-sidebar-empty">暂无教练<br />请先在「知识库」添加已批准的 Up 主</div>
          ) : (
            coaches.map(c => (
              <div
                key={c.id}
                className={`upmaster-sidebar-item ${selectedCoach?.id === c.id ? 'active' : ''}`}
                onClick={() => setSelectedCoach(c)}
              >
                <img
                  loading="lazy"
                  src={proxyImage(c.avatar, 40, 40)}
                  alt={c.name}
                  className="upmaster-sidebar-avatar"
                />
                <span className="upmaster-sidebar-main">
                  <span className="upmaster-sidebar-name">{c.name}</span>
                  <span className="upmaster-sidebar-count">{c.video_count ?? 0} 个视频</span>
                </span>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* ── 右栏：空状态 / 教练 header + 单窗口对话 ── */}
      <div className="kb-main flex-1 min-w-0 flex flex-col">
        {!selectedCoach ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="card empty-state">
              <div className="empty-state-icon">🏋️</div>
              <h3 className="empty-state-title">选择一位教练开始训练</h3>
              <p className="empty-state-desc">每位教练绑定专属知识库，对话互不干扰</p>
            </div>
          </div>
        ) : (
          <>
            <div className="page-header" style={{ padding: '12px 20px', margin: 0 }}>
              <div className="upmaster-header">
                <img
                  loading="lazy"
                  src={proxyImage(selectedCoach.avatar, 80, 80)}
                  alt={selectedCoach.name}
                  className="upmaster-avatar-sm"
                />
                <div className="upmaster-header-info">
                  <h2 className="upmaster-name">{selectedCoach.name}</h2>
                  <p className="upmaster-followers">{selectedCoach.bio || '你的私人健身电子教练'}</p>
                </div>
                <div className="upmaster-header-actions">
                  <button className="btn btn-sm btn-secondary" onClick={handleReset} title="清空当前对话历史并重新开始">
                    🔄 重置对话
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <ChatPanel
                upmasters={[]}
                conversationId={convData?.conversation_id ?? null}
                onConversationCreated={() => mutateConv(undefined, { revalidate: true })}
                onConversationUpdated={() => {}}
                coach={selectedCoach}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
